import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useNavigate} from 'react-router';
import UserAvatar from '../../../components/UserAvatar';
import {
    addWhiteboardLink,
    addWhiteboardChartLinkPair,
    removeWhiteboardLink,
} from '../../../firebase/firestore/whiteboards';
import {
    buildProductLink,
    filterLinksByType,
    linkTargetHref,
    WHITEBOARD_LINK_TYPES,
} from '../utils/whiteboardLinks';
import ProductTypeIcon from '../../../features/products/ProductTypeIcon';
import ChartLinkPicker from './ChartLinkPicker';
import ChartProductPicker from './ChartProductPicker';
import ChartIcrPicker from './ChartIcrPicker';
import {useChartRequirements} from '../../../hooks/useChartRequirements';
import {addIcrAssociation, removeIcrAssociation} from '../../../firebase/firestore/icr';

function RequirementLinkRow({link, onRemove, removing, navigate}) {
    const icrLabel = link.icrNumber && link.icrNumber !== link.title ? link.icrNumber : null;
    const authorName = link.ownerDisplayName || link.ownerId || null;
    const ownerUser = link.ownerId
        ? {userId: link.ownerId, displayName: link.ownerDisplayName || null}
        : null;

    return (
        <ListItem
            disablePadding
            secondaryAction={(
                <IconButton
                    edge="end"
                    size="small"
                    aria-label={`Remove link to ${link.title}`}
                    onClick={() => onRemove(link)}
                    disabled={removing}
                    sx={{color: 'text.disabled'}}
                >
                    <CloseIcon sx={{fontSize: 16}}/>
                </IconButton>
            )}
        >
            <ListItemButton
                dense
                onClick={() => link.href && navigate(link.href)}
                sx={{pr: 7, py: 1}}
            >
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{width: '100%', minWidth: 0}}>
                    <Stack spacing={0.25} sx={{flex: 1, minWidth: 0}}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{flex: 1, minWidth: 0, wordBreak: 'break-word'}}
                            >
                                {link.title || link.icrNumber || link.icrId}
                            </Typography>
                            {icrLabel ? (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{flexShrink: 0}}>
                                    {icrLabel}
                                </Typography>
                            ) : null}
                        </Stack>
                        {authorName && ownerUser && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <UserAvatar
                                    user={ownerUser}
                                    userId={link.ownerId}
                                    sx={{width: 14, height: 14, fontSize: 8}}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {authorName}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </ListItemButton>
        </ListItem>
    );
}

function LinkRow({link, onRemove, removing, navigate}) {
    const href = linkTargetHref(link);
    const linkedBy = link.linkedBy || null;
    const authorName = linkedBy?.displayName || linkedBy?.userId || null;

    return (
        <ListItem
            disablePadding
            secondaryAction={(
                <IconButton
                    edge="end"
                    size="small"
                    aria-label={`Remove link to ${link.title}`}
                    onClick={() => onRemove(link.id)}
                    disabled={removing}
                    sx={{color: 'text.disabled'}}
                >
                    <CloseIcon sx={{fontSize: 16}}/>
                </IconButton>
            )}
        >
            <ListItemButton
                dense
                onClick={() => href && navigate(href)}
                sx={{pr: 7, py: 1}}
            >
                <Stack direction="row" alignItems="flex-start" spacing={1} sx={{width: '100%', minWidth: 0}}>
                    {link.productType && (
                        <Box sx={{width: 14, height: 14, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '2px'}}>
                            <ProductTypeIcon productType={link.productType} sx={{ml: 0}}/>
                        </Box>
                    )}
                    <Stack spacing={0.25} sx={{flex: 1, minWidth: 0}}>
                        <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{wordBreak: 'break-word'}}
                        >
                            {link.title || link.targetId}
                        </Typography>
                        {authorName && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <UserAvatar
                                    user={linkedBy}
                                    userId={linkedBy.userId}
                                    sx={{width: 14, height: 14, fontSize: 8}}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {authorName}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </ListItemButton>
        </ListItem>
    );
}

function RequirementsSection({
    title,
    count,
    expanded,
    onToggle,
    requirements,
    loading,
    onAdd,
    adding,
    addControl,
    onRemove,
    removingAssocId,
    navigate,
    emptyMessage,
}) {
    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            elevation={0}
            sx={{
                '&:before': {display: 'none'},
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                sx={{
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': {my: 0.75},
                }}
            >
                <Typography variant="body2" fontWeight={600}>
                    {title} ({count})
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{px: 1, pt: 0, pb: 1.5}}>
                {loading ? (
                    <Typography variant="body2" color="text.secondary" sx={{px: 1, pb: 1}}>
                        Loading…
                    </Typography>
                ) : requirements.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{px: 1, pb: 1}}>
                        {emptyMessage}
                    </Typography>
                ) : (
                    <List disablePadding dense>
                        {requirements.map((link) => (
                            <RequirementLinkRow
                                key={link.id}
                                link={link}
                                onRemove={onRemove}
                                removing={removingAssocId === link.id}
                                navigate={navigate}
                            />
                        ))}
                    </List>
                )}
                <Box sx={{px: 1, pt: 0.5}}>
                    {adding ? addControl : (
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon/>}
                            onClick={onAdd}
                            disabled={loading}
                        >
                            Add
                        </Button>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

function LinksSection({
    title,
    count,
    expanded,
    onToggle,
    links,
    onAdd,
    adding,
    addControl,
    onRemove,
    removingLinkId,
    navigate,
    emptyMessage,
}) {
    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            elevation={0}
            sx={{
                '&:before': {display: 'none'},
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon/>}
                sx={{
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': {my: 0.75},
                }}
            >
                <Typography variant="body2" fontWeight={600}>
                    {title} ({count})
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{px: 1, pt: 0, pb: 1.5}}>
                {links.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{px: 1, pb: 1}}>
                        {emptyMessage}
                    </Typography>
                ) : (
                    <List disablePadding dense>
                        {links.map((link) => (
                            <LinkRow
                                key={link.id}
                                link={link}
                                onRemove={onRemove}
                                removing={removingLinkId === link.id}
                                navigate={navigate}
                            />
                        ))}
                    </List>
                )}
                <Box sx={{px: 1, pt: 0.5}}>
                    {adding ? addControl : (
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon/>}
                            onClick={onAdd}
                        >
                            Add
                        </Button>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

export default function WhiteboardLinksTab({
    whiteboardId,
    whiteboardTitle = 'Untitled',
    links = [],
    user,
    active = true,
    onLinksChanged,
}) {
    const navigate = useNavigate();
    const [expandedSection, setExpandedSection] = React.useState({
        products: true,
        charts: true,
        requirements: true,
    });
    const [addingProduct, setAddingProduct] = React.useState(false);
    const [addingChart, setAddingChart] = React.useState(false);
    const [addingRequirement, setAddingRequirement] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [removingLinkId, setRemovingLinkId] = React.useState(null);
    const [removingRequirementId, setRemovingRequirementId] = React.useState(null);
    const [error, setError] = React.useState('');

    const {
        requirements,
        loading: requirementsLoading,
        refetch: refetchRequirements,
    } = useChartRequirements(whiteboardId, Boolean(whiteboardId) && active);

    const productLinks = React.useMemo(
        () => filterLinksByType(links, WHITEBOARD_LINK_TYPES.PRODUCT),
        [links],
    );
    const chartLinks = React.useMemo(
        () => filterLinksByType(links, WHITEBOARD_LINK_TYPES.CHART),
        [links],
    );

    const linkedProductIds = React.useMemo(
        () => productLinks.map((link) => link.targetId),
        [productLinks],
    );
    const linkedChartIds = React.useMemo(
        () => chartLinks.map((link) => link.targetId),
        [chartLinks],
    );
    const linkedIcrIds = React.useMemo(
        () => requirements.map((req) => req.icrId).filter(Boolean),
        [requirements],
    );

    const handleSectionToggle = (section) => (_event, isExpanded) => {
        setExpandedSection((prev) => ({...prev, [section]: isExpanded}));
    };

    const handleAddProduct = async (option) => {
        const link = buildProductLink(option, user);
        if (!link || !whiteboardId) return;
        try {
            setError('');
            setSaving(true);
            const nextLinks = await addWhiteboardLink({
                whiteboardId,
                currentLinks: links,
                link,
            });
            onLinksChanged?.(nextLinks);
            setAddingProduct(false);
        } catch (err) {
            setError(err?.message || 'Failed to link product.');
        } finally {
            setSaving(false);
        }
    };

    const handleAddChart = async (chart) => {
        if (!whiteboardId) return;
        try {
            setError('');
            setSaving(true);
            const nextLinks = await addWhiteboardChartLinkPair({
                sourceWhiteboardId: whiteboardId,
                sourceTitle: whiteboardTitle,
                currentLinks: links,
                targetChart: chart,
                user,
            });
            onLinksChanged?.(nextLinks);
            setAddingChart(false);
        } catch (err) {
            setError(err?.message || 'Failed to link chart.');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveLink = async (linkId) => {
        if (!whiteboardId || removingLinkId) return;
        try {
            setError('');
            setRemovingLinkId(linkId);
            const nextLinks = await removeWhiteboardLink({
                whiteboardId,
                currentLinks: links,
                linkId,
            });
            onLinksChanged?.(nextLinks);
        } catch (err) {
            setError(err?.message || 'Failed to remove link.');
        } finally {
            setRemovingLinkId(null);
        }
    };

    const handleAddRequirement = async (icr) => {
        if (!whiteboardId || !icr?.id) return;
        try {
            setError('');
            setSaving(true);
            await addIcrAssociation(icr.id, {
                itemType: 'whiteboard',
                itemId: whiteboardId,
                itemTitle: icr.title || icr.icrNumber || '',
            });
            await refetchRequirements();
            setAddingRequirement(false);
        } catch (err) {
            setError(err?.message || 'Failed to link ICR.');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveRequirement = async (requirement) => {
        if (!requirement?.icrId || !requirement?.id || removingRequirementId) return;
        try {
            setError('');
            setRemovingRequirementId(requirement.id);
            await removeIcrAssociation(requirement.icrId, requirement.id);
            await refetchRequirements();
        } catch (err) {
            setError(err?.message || 'Failed to remove ICR link.');
        } finally {
            setRemovingRequirementId(null);
        }
    };

    return (
        <Stack height="100%" minHeight={0} overflow="hidden">
            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
                {error ? (
                    <Typography variant="body2" color="error" sx={{px: 2, pt: 1.5}}>
                        {error}
                    </Typography>
                ) : null}

                <LinksSection
                    title="Products"
                    count={productLinks.length}
                    expanded={expandedSection.products}
                    onToggle={handleSectionToggle('products')}
                    links={productLinks}
                    onAdd={() => setAddingProduct(true)}
                    adding={addingProduct}
                    addControl={(
                        <ChartProductPicker
                            active={addingProduct}
                            excludeIds={linkedProductIds}
                            onSelect={handleAddProduct}
                            onCancel={() => setAddingProduct(false)}
                            saving={saving}
                        />
                    )}
                    onRemove={handleRemoveLink}
                    removingLinkId={removingLinkId}
                    navigate={navigate}
                    emptyMessage="No products linked yet."
                />

                <LinksSection
                    title="Charts"
                    count={chartLinks.length}
                    expanded={expandedSection.charts}
                    onToggle={handleSectionToggle('charts')}
                    links={chartLinks}
                    onAdd={() => setAddingChart(true)}
                    adding={addingChart}
                    addControl={(
                        <ChartLinkPicker
                            active={addingChart}
                            whiteboardId={whiteboardId}
                            excludeIds={linkedChartIds}
                            onSelect={handleAddChart}
                            onCancel={() => setAddingChart(false)}
                            saving={saving}
                        />
                    )}
                    onRemove={handleRemoveLink}
                    removingLinkId={removingLinkId}
                    navigate={navigate}
                    emptyMessage="No charts linked yet."
                />

                <RequirementsSection
                    title="Requirements"
                    count={requirements.length}
                    expanded={expandedSection.requirements}
                    onToggle={handleSectionToggle('requirements')}
                    requirements={requirements}
                    loading={requirementsLoading}
                    onAdd={() => setAddingRequirement(true)}
                    adding={addingRequirement}
                    addControl={(
                        <ChartIcrPicker
                            active={addingRequirement}
                            excludeIds={linkedIcrIds}
                            onSelect={handleAddRequirement}
                            onCancel={() => setAddingRequirement(false)}
                            saving={saving}
                        />
                    )}
                    onRemove={handleRemoveRequirement}
                    removingAssocId={removingRequirementId}
                    navigate={navigate}
                    emptyMessage="No ICRs linked yet."
                />
            </Box>
        </Stack>
    );
}
